import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import React from 'react';
import clsx from 'clsx';

export interface SelectOption {
  name: string;
  value: string;
}

interface SelectProps {
  options?: Array<SelectOption>;
  label?: string;
  name: string;
  testId?: string;
  currentValue?: string[] | string;
  defaultValue?: string[] | string;
  clearable?: boolean;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  helperText?: string;
  error?: boolean;
  errorText?: string;
  onChange?: (value: string[] | string) => void;
  multiple?: boolean;
}

function CustomSelect(props: SelectProps) {
  const {
    options = [],
    label,
    name,
    testId,
    clearable = false,
    currentValue,
    defaultValue,
    required = false,
    disabled = false,
    fullWidth = false,
    size = 'medium',
    helperText,
    errorText,
    error = false,
    onChange,
    multiple = true,
  } = props;

  const id = `${name}-select`;
  const labelId = `${name}-label`;

  const value = React.useMemo(() => {
    if (multiple) {
      if (Array.isArray(currentValue)) return currentValue;
      if (typeof currentValue === 'string') return [currentValue];
      if (typeof defaultValue === 'string') return [defaultValue];
      if (Array.isArray(defaultValue)) return defaultValue;
      return [];
    } else {
      return typeof currentValue === 'string'
        ? currentValue
        : Array.isArray(currentValue)
          ? currentValue[0] || ''
          : typeof defaultValue === 'string'
            ? defaultValue
            : Array.isArray(defaultValue)
              ? defaultValue[0] || ''
              : '';
    }
  }, [currentValue, defaultValue, multiple]);

  const handleChange = (event: any) => {
    onChange?.(event.target.value);
  };

  return (
    <FormControl
      error={error}
      required={required}
      className={clsx('select-control', `select-control--${size}`, {
        'select-control--full-width': fullWidth,
      })}
    >
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select
        labelId={labelId}
        id={id}
        multiple={multiple}
        label={label}
        data-testid={testId}
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} id={id} />}
        MenuProps={{ disablePortal: true }}
        renderValue={(selected) =>
          multiple ? (
            <Box className="select-chips">
              {(selected as string[]).map((val) => {
                const option = options.find((opt) => opt.value === val);
                return <Chip key={val} label={option?.name || val} className="select-chip" />;
              })}
            </Box>
          ) : (
            <Typography variant="body2" className="select-value">
              {options.find((opt) => opt.value === selected)?.name || selected}
            </Typography>
          )
        }
        disabled={disabled}
      >
        {options.map((option) => {
          const isSelected = multiple ? (value as string[]).includes(option.value) : value === option.value;
          return (
            <MenuItem key={option.value} value={option.value}>
              {clearable ? (
                <>
                  <Checkbox checked={isSelected} />
                  <ListItemText primary={option.name} />
                </>
              ) : (
                <Typography variant="body2" className="select-menu-item">
                  {option.name}
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Select>
      {(helperText || errorText) && <FormHelperText>{error ? errorText : helperText}</FormHelperText>}
    </FormControl>
  );
}

export default CustomSelect;
